function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import * as React from 'react';
import { withInvertedTheme, InvertedTheme, useTheme } from '@veneer/theme';
import propTypes from './prop_types';
import useControlled from '../utils/use_controlled';
import mergeRefs from 'react-merge-refs';
import * as ReactDOM from 'react-dom';
import setRef from '../utils/set_ref';
import Popover from '../shared/popover';
import styles from './use_styles';
import useSafeTimeout from '../utils/use_safe_timeout';
import { jsx as ___EmotionJSX } from "@emotion/core";
var Tooltip = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  var {
    arrow = false,
    children,
    content,
    contentHideDelayOnHover = 300,
    contentShowDelayOnHover = 0,
    disabled = false,
    id,
    open: openProp,
    placement = 'bottom',
    portal = false,
    tabIndex = -1,
    useJsPositioning = false,
    userSelect = 'none'
  } = _ref;
  var [open, setOpen] = useControlled({
    controlled: openProp,
    default: false
  });
  var [childrenNode, setChildrenNode] = React.useState(null);
  var [arrowRef, setArrowRef] = React.useState();
  var theme = useTheme();
  var [setShowTimer, clearShowTimer] = useSafeTimeout();
  var [setHideTimer, clearHideTimer] = useSafeTimeout();
  var handleChildrenRef = mergeRefs([setChildrenNode, ref]);
  var handleReactComponentRef = React.useCallback(instance => {
    var value = ReactDOM.findDOMNode(instance);
    setRef(handleChildrenRef, value);
  }, [handleChildrenRef]);
  var handleRef = mergeRefs([children.props.ref, handleReactComponentRef]);
  var childrenProps = {
    'aria-describedby': open ? id : undefined,
    ref: handleRef
  };
  React.useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && childrenNode instanceof HTMLButtonElement && childrenNode.disabled) {
      console.error(['@veneer/core: You are providing to the Tooltip component a disabled `button` child.', 'A disabled element does not fire events and Tooltip needs to listen to the child', "element's events to show the content.", 'Add a simple wrapper element, such as a `span` or `div`.'].join('\n'));
    }
  }, [childrenNode]);
  var handleMouseOver = React.useCallback(event => {
    if (event.type === 'mouseover' && children.props.onMouseOver) {
      children.props.onMouseOver(event);
    }
    clearShowTimer();
    if (contentShowDelayOnHover) {
      event.persist();
      setShowTimer(() => {
        setOpen(true);
      }, contentShowDelayOnHover);
    } else {
      setOpen(true);
    }
  }, [children.props, clearShowTimer, contentShowDelayOnHover, setOpen, setShowTimer]);
  var handleMouseLeave = React.useCallback(event => {
    if (event.type === 'mouseleave' && children.props.onMouseLeave) {
      children.props.onMouseLeave(event);
    }
    clearShowTimer();
    clearHideTimer();
    event.persist();
    if (userSelect !== 'none') setOpen(false);else {
      setHideTimer(() => {
        setOpen(false);
      }, contentHideDelayOnHover);
    }
  }, [children.props, clearHideTimer, clearShowTimer, contentHideDelayOnHover, setHideTimer, setOpen, userSelect]);
  childrenProps.onMouseOver = handleMouseOver;
  childrenProps.onMouseLeave = handleMouseLeave;
  var popperModifiers = [{
    name: 'arrow',
    enabled: arrow,
    options: {
      element: arrowRef
    }
  }];
  var keepTooltipOnHoverCallbacks = userSelect !== 'none' ? {
    onMouseOver: handleMouseOver,
    onMouseLeave: handleMouseLeave
  } : {};
  return ___EmotionJSX(React.Fragment, null, ___EmotionJSX(InvertedTheme, null, /*#__PURE__*/React.cloneElement(children, childrenProps)), ___EmotionJSX(Popover, _extends({
    anchorEl: childrenNode,
    css: styles({
      arrow,
      open,
      userSelect,
      placement
    }),
    flip: useJsPositioning,
    id: id,
    keepMounted: true,
    modifiers: popperModifiers,
    open: open && !disabled,
    placement: placement,
    portal: portal,
    role: "tooltip",
    style: {
      zIndex: theme.elevation.tooltip
    },
    tabIndex: tabIndex
  }, keepTooltipOnHoverCallbacks), _ref2 => {
    var {
      placement: popoverPlacement
    } = _ref2;
    return ___EmotionJSX("div", {
      className: "vn-tooltip label-small",
      "data-placement": popoverPlacement.split('-')[0]
    }, content, arrow ? ___EmotionJSX("span", {
      className: "vn-tooltip__arrow",
      ref: setArrowRef
    }) : null);
  }));
});
Tooltip.propTypes = propTypes;
Tooltip.displayName = 'Tooltip';
export default withInvertedTheme(Tooltip);
export { Tooltip }; // export for react-docgen for manifest